import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["360° Walkthrough", "Aerial & Drone", "Both"];

const reveal = {
    initial: { opacity: 0, y: 36 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        message: "",
        project_type: "Both",
    });
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            toast.success("Received — we'll be in touch shortly.");
            setForm({
                name: "",
                email: "",
                message: "",
                project_type: "Both",
            });
        } catch {
            toast.error("Something went wrong — please try again.");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full border-b border-white/20 bg-transparent py-3 text-base font-light text-[#FAF8F5] placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-white/70";

    return (
        <section
            id="contact"
            className="bg-[#121211] px-6 py-24 text-[#FAF8F5] lg:px-10 lg:py-36"
            data-testid="contact-section"
        >
            <div className="mx-auto grid max-w-[1400px] gap-16 lg:grid-cols-2 lg:gap-24">
                <div>
                    <motion.p
                        {...reveal}
                        className="font-mono2 text-[10px] uppercase tracking-[0.34em] text-white/40"
                    >
                        Enquiries
                    </motion.p>
                    <motion.h2
                        {...reveal}
                        transition={{ ...reveal.transition, delay: 0.08 }}
                        className="mt-6 font-display text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl"
                    >
                        Start a<br />
                        <span className="italic">journey.</span>
                    </motion.h2>
                    <motion.p
                        {...reveal}
                        transition={{ ...reveal.transition, delay: 0.16 }}
                        className="mt-8 max-w-md text-base font-light leading-relaxed text-white/60"
                    >
                        Tell us about the space — a residence, a hotel, a
                        restaurant, a venue. We'll choreograph how the world
                        moves through it.
                    </motion.p>
                    <motion.div
                        {...reveal}
                        transition={{ ...reveal.transition, delay: 0.22 }}
                        className="mt-12 space-y-3 font-mono2 text-[11px] uppercase tracking-[0.24em] text-white/40"
                    >
                        <a data-testid="contact-email-link" href="mailto:hello@lilikoop.com" className="block transition-colors duration-300 hover:text-white">hello@lilikoop.com</a>
                        <p data-testid="contact-location">LOS ANGELES · WORLDWIDE</p>
                    </motion.div>
                </div>

                <motion.form
                    {...reveal}
                    transition={{ ...reveal.transition, delay: 0.12 }}
                    onSubmit={submit}
                    className="space-y-8"
                    data-testid="contact-form"
                >
                    <div>
                        <label className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/40">
                            Name
                        </label>
                        <input
                            data-testid="contact-name-input"
                            required
                            value={form.name}
                            onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                            }
                            placeholder="Your name"
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/40">
                            Email
                        </label>
                        <input
                            data-testid="contact-email-input"
                            required
                            type="email"
                            value={form.email}
                            onChange={(e) =>
                                setForm({ ...form, email: e.target.value })
                            }
                            placeholder="you@studio.com"
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/40">
                            Project type
                        </label>
                        <div className="mt-4 flex flex-wrap gap-3">
                            {TYPES.map((t) => (
                                <button
                                    type="button"
                                    key={t}
                                    data-testid={`contact-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    onClick={() =>
                                        setForm({ ...form, project_type: t })
                                    }
                                    className={`rounded-full border px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                                        form.project_type === t
                                            ? "border-white bg-white text-black"
                                            : "border-white/25 text-white/60 hover:border-white/60 hover:text-white"
                                    }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label htmlFor="contact-message" className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/40">
                            The space
                        </label>
                        <p id="contact-property-hint" data-testid="contact-property-hint" className="mt-2 text-xs font-light leading-relaxed text-white/50">
                            House, Restaurant, Hotel, Retail, Venue, Commercial Space · Approx. size / sq ft
                        </p>
                        <textarea
                            id="contact-message"
                            aria-describedby="contact-property-hint"
                            data-testid="contact-message-input"
                            required
                            rows={4}
                            value={form.message}
                            onChange={(e) =>
                                setForm({ ...form, message: e.target.value })
                            }
                            placeholder="Property type, approximate square footage, location, key features, and what you want visitors to experience."
                            className={`${inputCls} resize-none`}
                        />
                    </div>
                    <button
                        data-testid="contact-submit-button"
                        type="submit"
                        disabled={sending}
                        className="group relative w-full overflow-hidden rounded-full border border-white/30 py-4 font-mono2 text-[11px] uppercase tracking-[0.3em] text-white transition-colors duration-500 hover:text-black disabled:opacity-50 sm:w-auto sm:px-12"
                    >
                        <span className="absolute inset-0 -z-0 translate-y-full bg-white transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0" />
                        <span className="relative">
                            {sending ? "Sending…" : "Send enquiry"}
                        </span>
                    </button>
                </motion.form>
            </div>
        </section>
    );
}
